import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import { Save, Image as ImageIcon, Palette, Sparkles, RotateCcw } from 'lucide-react';

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const PRESETS = [
  { name: 'Rosa Disney', primary: '#EC4899', accent: '#D8B4E2' },
  { name: 'Coral suave', primary: '#FB7185', accent: '#FDBA74' },
  { name: 'Lila lavanda', primary: '#A78BFA', accent: '#F0ABFC' },
  { name: 'Menta fresca', primary: '#10B981', accent: '#A7F3D0' },
  { name: 'Cielo dulce', primary: '#3B82F6', accent: '#93C5FD' },
  { name: 'Dorado glam', primary: '#F59E0B', accent: '#FDE68A' },
  { name: 'Berry oscuro', primary: '#BE185D', accent: '#F472B6' },
];

const shadeHex = (hex, delta) => {
  if (!hex || !hex.startsWith('#')) return hex;
  const h = hex.replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  let r = parseInt(n.slice(0, 2), 16);
  let g = parseInt(n.slice(2, 4), 16);
  let b = parseInt(n.slice(4, 6), 16);
  r = Math.max(0, Math.min(255, r + delta));
  g = Math.max(0, Math.min(255, g + delta));
  b = Math.max(0, Math.min(255, b + delta));
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
};

const livePreview = (primary, accent) => {
  const root = document.documentElement;
  if (primary) {
    root.style.setProperty('--gifts-primary', primary);
    root.style.setProperty('--gifts-primary-hover', shadeHex(primary, -25));
    root.style.setProperty('--gifts-primary-light', shadeHex(primary, 50));
  }
  if (accent) root.style.setProperty('--gifts-accent', accent);
};

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const onLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Máximo 2MB'); return; }
    const url = await fileToDataUrl(file);
    setForm((f) => ({ ...f, logo_url: url }));
  };

  const setColor = (key, value) => {
    const next = { ...form, [key]: value };
    setForm(next);
    livePreview(next.primary_color, next.accent_color);
  };

  const applyPreset = (preset) => {
    const next = { ...form, primary_color: preset.primary, accent_color: preset.accent };
    setForm(next);
    livePreview(next.primary_color, next.accent_color);
  };

  const resetColors = () => {
    const next = { ...form, primary_color: '#EC4899', accent_color: '#D8B4E2' };
    setForm(next);
    livePreview(next.primary_color, next.accent_color);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings', {
        slogan: form.slogan,
        whatsapp_number: form.whatsapp_number,
        logo_url: form.logo_url,
        hero_subtitle: form.hero_subtitle,
        primary_color: form.primary_color,
        accent_color: form.accent_color,
      });
      await refresh();
      toast.success('Ajustes guardados ✨');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-settings-page" className="max-w-3xl space-y-6">
      <div>
        <h1 className="font-display text-3xl text-slate-900 mb-1">Ajustes del sitio</h1>
        <p className="text-slate-500">Personaliza marca, contacto y colores</p>
      </div>

      {/* BRAND */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <h2 className="font-display text-xl text-slate-900 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-pinky-500" /> Marca
        </h2>

        <div>
          <Label>Logo</Label>
          <div className="mt-1 flex items-center gap-4">
            {form.logo_url ? (
              <img src={form.logo_url} alt="Logo" className="h-24 w-24 rounded-2xl object-contain bg-pinky-50 border border-slate-200" />
            ) : (
              <div className="h-24 w-24 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 border-2 border-dashed border-slate-300"><ImageIcon className="h-8 w-8" /></div>
            )}
            <div>
              <input type="file" accept="image/*" onChange={(e) => onLogo(e.target.files?.[0])} className="text-sm" data-testid="settings-logo-input" />
              <p className="text-xs text-slate-500 mt-1">PNG recomendado (máx 2MB)</p>
            </div>
          </div>
        </div>

        <div>
          <Label>Slogan principal</Label>
          <Input value={form.slogan || ''} onChange={(e) => setForm({ ...form, slogan: e.target.value })} className="rounded-xl mt-1" data-testid="settings-slogan-input" />
        </div>

        <div>
          <Label>Sub-slogan / banner secundario</Label>
          <Input value={form.hero_subtitle || ''} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} className="rounded-xl mt-1" placeholder="Entrega inmediata en Guatemala 🇬🇹" data-testid="settings-hero-input" />
        </div>

        <div>
          <Label>WhatsApp (con código de país, ej. +50237835330)</Label>
          <Input value={form.whatsapp_number || ''} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} className="rounded-xl mt-1" data-testid="settings-whatsapp-input" />
        </div>
      </section>

      {/* COLORS */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-display text-xl text-slate-900 flex items-center gap-2">
            <Palette className="h-4 w-4 text-pinky-500" /> Colores de la marca
          </h2>
          <Button variant="outline" onClick={resetColors} className="rounded-full text-xs gap-1" data-testid="settings-reset-colors">
            <RotateCcw className="h-3 w-3" /> Restaurar
          </Button>
        </div>

        <p className="text-sm text-slate-500">Cambia el color principal de los botones y acentos. La vista previa se aplica inmediatamente.</p>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Color principal (botones)</Label>
            <div className="mt-2 flex items-center gap-3">
              <input
                type="color"
                value={form.primary_color || '#EC4899'}
                onChange={(e) => setColor('primary_color', e.target.value)}
                className="h-12 w-16 rounded-xl border border-slate-200 cursor-pointer"
                data-testid="settings-primary-color"
              />
              <Input
                value={form.primary_color || ''}
                onChange={(e) => setColor('primary_color', e.target.value)}
                className="rounded-xl font-mono text-sm flex-1"
                placeholder="#EC4899"
              />
            </div>
          </div>
          <div>
            <Label>Color de acento (sparkles, detalles)</Label>
            <div className="mt-2 flex items-center gap-3">
              <input
                type="color"
                value={form.accent_color || '#D8B4E2'}
                onChange={(e) => setColor('accent_color', e.target.value)}
                className="h-12 w-16 rounded-xl border border-slate-200 cursor-pointer"
                data-testid="settings-accent-color"
              />
              <Input
                value={form.accent_color || ''}
                onChange={(e) => setColor('accent_color', e.target.value)}
                className="rounded-xl font-mono text-sm flex-1"
                placeholder="#D8B4E2"
              />
            </div>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Paletas predefinidas</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="color-presets">
            {PRESETS.map((p) => (
              <button
                key={p.name}
                onClick={() => applyPreset(p)}
                className={`px-3 py-2 rounded-full border-2 text-xs font-semibold transition-all flex items-center gap-2 ${
                  form.primary_color === p.primary ? 'border-slate-800 bg-slate-50' : 'border-slate-200 hover:border-slate-400'
                }`}
                data-testid={`preset-${p.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <span className="flex">
                  <span className="h-4 w-4 rounded-full" style={{ background: p.primary }} />
                  <span className="h-4 w-4 rounded-full -ml-1.5 border-2 border-white" style={{ background: p.accent }} />
                </span>
                {p.name}
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Vista previa</p>
          <div className="flex flex-wrap gap-2">
            <Button className="btn-pink rounded-full">Botón principal</Button>
            <Button className="btn-pink-outline rounded-full">Botón secundario</Button>
            <Button className="btn-pink rounded-full" disabled>Deshabilitado</Button>
          </div>
        </div>
      </section>

      <Button onClick={save} disabled={saving} className="btn-pink rounded-full gap-2 h-12 px-8" data-testid="settings-save-btn">
        <Save className="h-4 w-4" /> {saving ? 'Guardando…' : 'Guardar todos los cambios'}
      </Button>
    </div>
  );
}
